import { useState } from "react"
import Cookies from "js-cookie"
import * as XLSX from "xlsx"
import { Upload, Loader2, AlertCircle, FileSpreadsheet } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

import { uploadFileCustom } from "../Scripts/fileUpload.js"

// ─────────────────────────────────────────────────────────────────────────
// Excel/CSV bulk import for the Subject Registry — separate from (and much
// simpler than) the generic Teacher/Student CSVOperationsHandler, since
// subjects only ever need two columns and none of that component's other
// options (alumni archiving, admin-add flags, email column) apply here.
// Reuses the same underlying upload mechanism though: read the file's
// header row client-side, let the admin map which column is which, then
// upload a JSON manifest + the raw file straight to Cloud Storage — a
// background Cloud Function (subject_import_function in index.js) picks
// it up from there. This is fire-and-forget, same as the existing
// Teacher/Student import: upload, wait a moment, refresh the subjects
// list to see what landed.
// ─────────────────────────────────────────────────────────────────────────

type Props = {
    open: boolean
    onClose: () => void
    // Optional: also add every imported subject to this programme (the
    // server links new codes and existing ones alike; nothing is copied)
    programmeId?: string
    programmeName?: string
}

function readHeaderRow(file: File): Promise<string[]> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer)
                const workbook = XLSX.read(data, { type: "array" })
                const sheet = workbook.Sheets[ workbook.SheetNames[ 0 ] ]
                const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as string[][]
                if (rows.length > 0) resolve(rows[ 0 ].map(String))
                else reject("Sheet is empty")
            } catch (err: any) {
                reject("Failed to read file: " + err.message)
            }
        }
        reader.onerror = () => reject("Failed to load file")
        reader.readAsArrayBuffer(file)
    })
}

export function SubjectImportDialog({ open, onClose, programmeId, programmeName }: Props) {
    const [ file, setFile ] = useState<File | null>(null)
    const [ headers, setHeaders ] = useState<string[]>([])
    const [ codeColumn, setCodeColumn ] = useState<string>("")
    const [ nameColumn, setNameColumn ] = useState<string>("")
    const [ reading, setReading ] = useState(false)
    const [ uploading, setUploading ] = useState(false)
    const [ error, setError ] = useState<string | null>(null)
    const [ done, setDone ] = useState(false)

    const reset = () => {
        setFile(null); setHeaders([]); setCodeColumn(""); setNameColumn("")
        setError(null); setDone(false)
    }

    const handleFilePick = async (f: File | null) => {
        reset()
        if (!f) return
        setFile(f)
        setReading(true)
        try {
            const cols = await readHeaderRow(f)
            setHeaders(cols)
        } catch (e: any) {
            setError(typeof e === "string" ? e : e.message)
        } finally {
            setReading(false)
        }
    }

    const handleUpload = async () => {
        if (!file || !codeColumn) {
            setError("Pick a file and select which column has the subject code.")
            return
        }
        setUploading(true)
        setError(null)
        try {
            const authToken = Cookies.get("authToken") || ""
            const uid = Cookies.get("user_uid") || ""
            const insCode = Cookies.get("ins_code") || ""

            const manifest = { codeColumn, nameColumn: nameColumn || null, ...(programmeId ? { programmeId } : {}) }
            const manifestFilename = file.name.replace(/\.[^/.]+$/, "") + ".json"
            const manifestBlob = new Blob([ JSON.stringify(manifest, null, 2) ], { type: "application/json" })
            const manifestFile = new File([ manifestBlob ], manifestFilename, { type: "application/json" })

            // Manifest first — the background trigger checks for it before
            // processing the data file, same ordering the Teacher/Student
            // importer relies on.
            await uploadFileCustom(manifestFile, `${insCode}/csv/subject_add/${uid}/${manifestFilename}`, authToken)
            await uploadFileCustom(file, `${insCode}/csv/subject_add/${uid}/${file.name}`, authToken)

            setDone(true)
        } catch (e: any) {
            setError(e.message || "Upload failed.")
        } finally {
            setUploading(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose() } }}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileSpreadsheet className="h-5 w-5" /> Import Subjects{programmeName ? ` into ${programmeName}` : ""}
                    </DialogTitle>
                </DialogHeader>

                {done ? (
                    <div className="py-4 space-y-3">
                        <p className="text-sm text-muted-foreground">
                            Import started — this runs in the background. Give it a moment, then refresh the
                            subjects list to see what was added. Rows with a blank code are skipped
                            automatically; {programmeId
                                ? "codes that already exist are added to this programme instead of copied."
                                : "duplicate codes are skipped."}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4 py-2">
                        {error && (
                            <p className="text-sm text-red-600 flex items-center gap-1.5">
                                <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {error}
                            </p>
                        )}

                        <div>
                            <Label>Excel or CSV file</Label>
                            <input
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                onChange={(e) => handleFilePick(e.target.files?.[ 0 ] || null)}
                                className="mt-1 text-xs"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                First row should be column headers (e.g. "Code", "Name").
                            </p>
                        </div>

                        {reading && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading file…
                            </p>
                        )}

                        {headers.length > 0 && (
                            <>
                                <div>
                                    <Label>Subject Code Column</Label>
                                    <Select value={codeColumn} onValueChange={setCodeColumn}>
                                        <SelectTrigger className="mt-1"><SelectValue placeholder="Select column…" /></SelectTrigger>
                                        <SelectContent>
                                            {headers.map((h, i) => <SelectItem key={i} value={h}>{h}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Subject Name Column (optional)</Label>
                                    <Select value={nameColumn} onValueChange={setNameColumn}>
                                        <SelectTrigger className="mt-1"><SelectValue placeholder="None" /></SelectTrigger>
                                        <SelectContent>
                                            {headers.map((h, i) => <SelectItem key={i} value={h}>{h}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => { reset(); onClose() }}>
                        {done ? "Close" : "Cancel"}
                    </Button>
                    {!done && (
                        <Button onClick={handleUpload} disabled={uploading || !file || !codeColumn} className="gap-1.5">
                            {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</> : <><Upload className="h-4 w-4" /> Import</>}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
